import { useState, useEffect, useCallback } from 'react';
import { apiUtils } from '../../../utils';

/**
 * Scan-detail data: the scan document plus its flagged posts.
 *
 * One implementation of each fetch. The previous version inlined the same two
 * GETs three separate times (initial load, poll loop, terminal reconcile),
 * which is how a fix applied to one copy kept missing the others.
 *
 * Polling here is a fallback while the scan is ACTIVE only - live updates
 * arrive over the WebSocket via ScanProgress; this catches the gap if the
 * socket is down. It pauses while the tab is hidden: a manager who leaves a
 * scan open overnight should cost zero requests.
 *
 * @param {string} scanId - The scan ID to fetch data for
 */
export const useScanData = (scanId) => {
	const [scan, setScan] = useState(null);
	const [flaggedPosts, setFlaggedPosts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const fetchScan = useCallback(async () => {
		if (!scanId) return;
		try {
			const response = await apiUtils.get(`/api/scans/${scanId}`);
			if (!response.ok) {
				throw new Error(response.status === 404 ? 'Scan not found' : `Failed to fetch scan (${response.status})`);
			}
			const data = await response.json();
			setScan(data.data);
		} catch (err) {
			setError(err.message);
		}
	}, [scanId]);

	const fetchFlaggedPosts = useCallback(async () => {
		if (!scanId) return;
		try {
			const response = await apiUtils.get(`/api/scans/${scanId}/posts`, {
				params: { flaggedOnly: true, limit: 100 },
			});
			if (response.ok) {
				const data = await response.json();
				setFlaggedPosts(data.data?.posts || []);
			}
		} catch (err) {
			// Posts degrade the page, they don't fail it.
			console.error('Error fetching flagged posts:', err);
		}
	}, [scanId]);

	const refresh = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			await Promise.all([fetchScan(), fetchFlaggedPosts()]);
		} finally {
			setLoading(false);
		}
	}, [fetchScan, fetchFlaggedPosts]);

	// Initial load
	useEffect(() => {
		if (!scanId) return;
		setLoading(true);
		setError(null);
		Promise.all([fetchScan(), fetchFlaggedPosts()]).finally(() => setLoading(false));
	}, [scanId, fetchScan, fetchFlaggedPosts]);

	// Fallback poll while the scan is active. Skips ticks while the tab is
	// hidden - document.visibilitychange was checked NOWHERE in this app, so
	// every background tab kept polling forever.
	useEffect(() => {
		if (!scanId) return;
		const isActive = ['pending', 'processing', 'analyzing'].includes(scan?.status);
		if (!isActive) return;

		const tick = () => {
			if (document.hidden) return;
			fetchScan();
			fetchFlaggedPosts();
		};
		const interval = setInterval(tick, 10000);
		// Catch up immediately when the user returns to the tab.
		const onVisible = () => {
			if (!document.hidden) tick();
		};
		document.addEventListener('visibilitychange', onVisible);
		return () => {
			clearInterval(interval);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, [scan?.status, scanId, fetchScan, fetchFlaggedPosts]);

	// Final reconciliation on terminal status: the analysis webhook writes
	// per-post flags and final results AT or just after the flip, so one more
	// delayed pull prevents "5 flagged" over an empty list until manual reload.
	useEffect(() => {
		if (!scanId) return;
		if (scan?.status !== 'completed' && scan?.status !== 'failed') return;
		const t = setTimeout(() => {
			fetchScan();
			fetchFlaggedPosts();
		}, 4000);
		return () => clearTimeout(t);
	}, [scan?.status, scanId, fetchScan, fetchFlaggedPosts]);

	const isScanning = ['pending', 'processing', 'analyzing'].includes(scan?.status);
	const isCompleted = scan?.status === 'completed';
	const isFailed = scan?.status === 'failed';

	return {
		scan,
		flaggedPosts,
		loading,
		error,
		refresh,
		fetchScan,
		fetchFlaggedPosts,
		isScanning,
		isCompleted,
		isFailed,
	};
};
